import { LitElement, html, css } from 'lit';
import { msg } from '@lit/localize';
import { hideRevealInputs, validateForm,validationHandler,validationHandlerClear } from '../../utilities/formsUtilities';
import { accessibility,formStyles } from '../../utilities/styles';
import { setFileValueFromDataUrl } from '../../utilities/formsUtilities';
import { EventManager } from '../../utilities/events';

export class AllInPagedFormPage extends LitElement {
    static properties = {
        title: { type: String },
        formName: { type: String },
        uid: { type: String },
        validate: { 
            type: Boolean,
            reflect: true 
        },
        showHeader: { type: Boolean },
        getSaved: { type: String},
        hasSubPages: { type: Boolean, reflect: true },
        subPages: { type: Array, state: true },
        currentSubPage: { type: Number },
        current: { type: Boolean, reflect: true },
        isMasterPage: {type: Boolean, reflect: true}
    }

    static get formAssociated() {
        return true;
      }

    constructor() {
        super();
        this._internals = this.attachInternals();
    }
    
    async connectedCallback(){
        super.connectedCallback();
        await new Promise((resolve) => requestAnimationFrame(resolve));
        let slots = this.renderRoot.querySelectorAll('slot');
        slots?.forEach(slot=>{
            slot.addEventListener('change',this._slotInputChange.bind(this))
        });
        this.eventManager = new EventManager(this);
        let urlInputs = [];
        let phoneInputs = [];
        urlInputs.forEach(input=>{
            input.dataset.hasListener = 'true';
            input.addEventListener('blur',this._slotURLChange.bind(this));
        });

        phoneInputs.forEach(input=>{
            input.dataset.hasListener = 'true';
            input.addEventListener('blur',e=>{
                e.preventDefault();
                let value = e.target.value;

                // if empty clear errors and exit
                if(value === ""){
                    e.target.removeAttribute('aria-invalid');
                    e.target.removeAttribute('aria-describedby');
                    e.target.classList.remove('error');
                    let span = document.getElementById(e.target.id + '_error');
                    if(span) span.remove();
                    return;
                }

                //Regex to check if value is only numbers
                let regex = /^[0-9\s+]+$/;
                if(!regex.test(value)){
                    if(e.target.classList.contains('error')) return;
                    e.target.setAttribute('aria-invalid','true');
                    e.target.setAttribute('aria-describedby',e.target.id + '_error');
                    e.target.classList.add('error');
                    let span = document.createElement('span');
                    span.id=e.target.id + '_error';
                    span.classList.add('error_message');
                    let message = msg(`Please enter a valid phone number, using numbers only`);
                    span.innerHTML = message;
                    e.target.parentNode.appendChild(span);
                }
                else{
                    e.target.removeAttribute('aria-invalid');
                    e.target.removeAttribute('aria-describedby');
                    e.target.classList.remove('error');
                    let span = document.getElementById(e.target.id + '_error');
                    if(span){
                        span.remove();
                    }
                }
            });
        });
        if(this.hasSubPages){
            this._processSubPages();
        }
    }

    get formResponse() {
        return this.shadowRoot.querySelector('.message');
    }

    get _slottedChildren() {
        let slot;
        if(this.showHeader){
            slot = this.renderRoot.querySelectorAll('slot')[1];
        }
        else{
            slot = this.renderRoot.querySelector('slot');
        }
        return slot.assignedElements({flatten: true});
    }

    get _slot() {
        return this.shadowRoot.querySelector('slot');
    }

    get fields(){
        let children = this._slottedChildren;
        let _fields = children.map(el=>{
            if(el.tagName=='INPUT'||el.tagName=='TEXTAREA'||el.tagName=='SELECT'||el.tagName=='ALLIN-IMAGE-UPLOADER'||el.tagName=='ALLIN-MARKDOWN-EDITOR'){
                return el;
            }
            return el.querySelectorAll('input,textarea,select,allin-image-uploader,allin-markdown-editor');
        }).filter(el=>el);
        let fields = [];
        _fields.forEach(el=>{
            if(el.length){
                el.forEach(i=>{
                    fields.push(i);
                })
            }
            else{
                fields.push(el);
            }
        });
        return fields;
    }

    render() {
        if(this.showHeader){
            return html` 
                <div class="header"><slot name="headers"></slot></div>
                <div class="message notice" aria-live="polite"></div>
                <div class="page">
                    
                    <slot></slot>
                </div>
            `
        }
        return html` 
            <div class="message notice" aria-live="polite"></div>
            <div class="page">
                <h2 class="screen-reader-text">${this.title}</h2>
                
                <slot></slot>
            </div>
        `
    }

    updated(changedProperties) {
        if(changedProperties.has('validate')){
            if(this.validate){
                this._validatePage();
            }
        }
        if(changedProperties.has('formName')){
            this._maybeFillFieldsFromLocalStorage();
        }
        let urlInputs = [];
        this._slottedChildren?.forEach(el=>{
            let _urlInputs = el.querySelectorAll('input[type="url"]');
            if(_urlInputs.length){
                _urlInputs.forEach(input=>{
                    if(input.dataset.hasListener) return;
                    urlInputs.push(input);
                });
            }
        });
        urlInputs.forEach(input=>{
            input.dataset.hasListener = 'true';
            input.addEventListener('blur',this._slotURLChange.bind(this));
        });
        if(changedProperties.has('hasSubPages')){
            this._processSubPages();
        }
        if(changedProperties.has('currentSubPage')){
            this._showCurrentSubPage();
        }
    }

    _maybeFillFieldsFromLocalStorage() {
        if(this.getSaved==="false") return;
        let formData = JSON.parse(localStorage.getItem(`allin-paged-form-${this.formName}-${this.uid}`));
        if(formData){
            this.fields.forEach(field=>{
                if(field.tagName=='INPUT'||field.tagName=='TEXTAREA'||field.tagName=='SELECT'){
                    let value = formData[field.name];
                    if(value){
                        if(field.type=='file'){
                            if(value.hasOwnProperty('publicUrl')&&value.publicUrl){
                                let uploader = field.closest('allin-image-uploader');
                                let hiddenInput = uploader?.querySelector(`input[name='alt_${field.name}']`);
                                if(!hiddenInput){
                                    hiddenInput = document.createElement('input');
                                    hiddenInput.type = 'hidden';
                                    hiddenInput.name = `alt_${field.name}`;
                                    uploader?.appendChild(hiddenInput);
                                }
                                let fileId = value.id || '';
                                hiddenInput.value = fileId;
                                uploader.querySelector('img').src = value.publicUrl;
                            }
                            else{
                                setFileValueFromDataUrl(field,value);
                            }
                        }
                        else if(field.type=='checkbox'){
                            field.checked = value;
                        }
                        else if (field.type=='radio'){
                            if(field.value == value){
                                field.checked = true;
                            }
                        }
                        else if(field.dataset.type=='multiselect'){
                            let multiselect = field.closest('allin-multiselect');

                            if(value){
                                if(Array.isArray(value)){
                                    multiselect.updateValueExternally(value);
                                }
                                else{
                                    multiselect.updateValueExternally(value.split(','));
                                }
                            }
                        }
                        else if(field.type=='url'){
                            field.value = value;
                            field.setAttribute('value',field.value);
                        }
                        else if(field.type=='hidden' && field.closest('allin-markdown-editor')){
                            field.value = value;
                            let markdownEditor = field.closest('allin-markdown-editor');
                            markdownEditor.value = value;
                        }
                        else{
                            field.value = value;
                        }
                    }
                    if(field.tagName=='SELECT'){
                        let options = field.querySelectorAll('option');
                        options.forEach(option=>{
                            if(option.value == value){
                                option.selected = true;
                            }
                        });
                    }
                    if(field.tagName=='TEXTAREA'){
                        field.dispatchEvent(new Event('blur'));
                    }
                }
                // if(field.tagName=='ALLIN-IMAGE-UPLOADER'){
                //     if(formData[field.inputname]){
                //         let value = formData[field.inputname];
                //         if(value.hasOwnProperty('publicUrl')){
                //             let uploader = field.closest('allin-image-uploader');
                //             let hiddenInput = uploader?.querySelector(`input[name='alt_${field.inputname}']`);
                //             if(!hiddenInput){
                //                 hiddenInput = document.createElement('input');
                //                 hiddenInput.type = 'hidden';
                //                 hiddenInput.name = `alt_${field.inputname}`;
                //                 uploader?.appendChild(hiddenInput);
                //             }
                //             let fileId = '';
                //             let pattern = /%2f([^%]*)%2f/gi;
                //             let matches = pattern.exec(value.publicUrl);
                //             if(matches && matches[1]){
                //                 fileId = matches[1];
                //             }
                //             hiddenInput.value = fileId;
                //             uploader.querySelector('img').src = value.publicUrl;
                //             uploader.triggerExternalFileLoaded();
                //         }
                //         else{
                //             setFileValueFromDataUrl(field.input,value);
                //         }
                //     }
                // }
                if(field.tagName=='ALLIN-IMAGE-UPLOADER') {
                    const pagedForm = this.closest('allin-paged-form'); // AllInPagedForm instance

                    if(pagedForm._filesMap?.has(field.inputname)) {
                        // Restore from _filesMap
                        const savedFile = pagedForm._filesMap.get(field.inputname);
                        const uploader = field.closest('allin-image-uploader');
                        uploader.querySelector('img').src = savedFile.dataURL;
                        uploader.querySelector('img').dataset.has_src = "true";
                        uploader.input.style.display = 'none';

                        // Restore ALT if available
                        if(savedFile.alt && field.altrequired) {
                            const root = field.getRootNode();
                            const altInput = root.querySelector(`#${field.altrequired}`);
                            if(altInput) altInput.value = savedFile.alt;
                        }

                        uploader.triggerExternalFileLoaded();
                    } 
                    else if(formData[field.inputname]) {
                        // existing localStorage restoration (leave as-is)
                        const value = formData[field.inputname];
                        if(value.hasOwnProperty('publicUrl')&&value.publicUrl){
                            const uploader = field.closest('allin-image-uploader');
                            let hiddenInput = uploader?.querySelector(`input[name='alt_${field.inputname}']`);
                            if(!hiddenInput){
                                hiddenInput = document.createElement('input');
                                hiddenInput.type = 'hidden';
                                hiddenInput.name = `alt_${field.inputname}`;
                                uploader?.appendChild(hiddenInput);
                            }
                            let fileId = value.id || '';
                            hiddenInput.value = fileId;
                            uploader.querySelector('img').src = value.publicUrl;
                            uploader.triggerExternalFileLoaded();
                        } else {
                            setFileValueFromDataUrl(field.input,value);
                        }
                    }
                }
            });
            hideRevealInputs(this._slottedChildren);
        }
    }
    
    _slotInputChange(e) {
        if(e.target.classList.contains('error')) return;
        this._internals.setFormValue(e.target.name,e.target.value);
        validationHandlerClear(e.target);
    }

    _slotURLChange(e) {
        if(e.target.type=='url'){
            e.target.setAttribute('value',e.target.value);
            if(e.target.value=='') return;
            try{
                const currentUrl = new URL(e.target.value);
                const { protocol } = currentUrl;

                if (protocol !== 'http:' && protocol !== 'https:') {
                    currentUrl.protocol = 'https:';
                    e.target.value = currentUrl.toString();
                    e.target.setAttribute('value',e.target.value);
                }
            }
            catch(error){
                e.target.value = `https://${e.target.value}`;
                e.target.setAttribute('value',e.target.value);
            }
        }
     }

     _slotPhoneChange(e) {
        if(e.target.classList.contains('error')) return;
        this._internals.setFormValue(e.target.name,e.target.value);
        validationHandlerClear(e.target);
    }

    _validatePage(e) {
        let internalsValidity = this._internals.checkValidity();
        let _inputs = this._slottedChildren.map(el=>{
            if(el.tagName=='INPUT'||el.tagName=='TEXTAREA'||el.tagName=='SELECT'){
                return el;
            }
            if(el.tagName=='ALLIN-IMAGE-UPLOADER'){
                return el.input;
            }
            if(el.tagName=='ALLIN-MULTISELECT'){
                return el.querySelector('input[type="hidden"]');
            }
            return el.querySelectorAll('input,textarea,select')
        }).filter(el=>el);
        let inputs = [];
        _inputs.forEach(el=>{
            if(el instanceof NodeList){
                el.forEach(i=>{
                    inputs.push(i);
                })
            }
            else{
                inputs.push(el);
            }
        });
        inputs.forEach(el=>{
            validationHandlerClear(el);
            let validity =  el.reportValidity();
            if(!validity){
            validationHandler(el,el.validationMessage);
            internalsValidity = false;
            }
        })
        let valid = validateForm(inputs,this.formResponse);
        if(internalsValidity && valid){
            this._dispatchValidityEvent(true);
        }
        else{
            this._dispatchValidityEvent(false);
        }
        this.validate = false;
        this.scrollIntoView({behavior:'smooth',block:'start'});
    }

    _dispatchValidityEvent(validity) {
        this.eventManager.emit('allin-page-valid',{el:this.shadowRoot.host,validity:validity});
    }

    reprocessSubPages(){
        if(this.hasSubPages){
            this._processSubPages();
        }
    }

    async _processSubPages(){
        this.subPages = [];
        this._slottedChildren?.forEach(async el=>{
            if(this.hasSubPages){
                if(el.tagName=='ALLIN-PAGED-FORM-SUBPAGE'){
                    this.subPages.push(el);  
                }
            }
        });
        await Promise.all(this.subPages.map((el)=>{
            el.updateComplete
        }));
        this.subPages = this.subPages.filter(el=>el.getShowValue())

        //Only show message when there are no provisions
        const hasProvisions = this.subPages.length > 0;

        this._slottedChildren.forEach(el => {
            if (el.classList?.contains('js-no-access-needs')) {
                el.style.display = hasProvisions ? 'none' : 'block';
            }
        });

        this.subPages.forEach(el=>{
            el.setRevealState(false);
        });
        this.eventManager.emit('allin-page-subpages-processed',{el:this,subPages:this.subPages});
        if(this.hasSubPages&&this.current){
            this._showCurrentSubPage();
        }
        
    }

    _showCurrentSubPage(){
        if(this.hasSubPages&&this.current){
            if(!this.currentSubPage||this.currentSubPage>=this.subPages.length){
                this.currentSubPage = 0;
            }
            this.subPages.forEach((el,index)=>{
                if(index!=this.currentSubPage){
                    el.setRevealState(false);
                }
                else{
                    el.setRevealState(true);
                }
            });
            this.eventManager.emit('allin-page-subpage-shown',{el:this,subPage:this.subPages[this.currentSubPage],index:this.currentSubPage});
        }
    }

    setCurrentSubPage(index){
        if(index!==this.currentSubPage){
            this.currentSubPage = index;
        }
    }

    subPagesCount(){
        return this.subPages.length;
    }

    getCurrentSubPage(){
        return this.currentSubPage;
    }

    isSubPageParent(){
        return this.hasSubPages;
    }

    isMasterPageParent(){
        return this.isMasterPage;
    }

    async getSubPages(){
        if(this.hasSubPages){
            if(!this.subPages){
                await this._processSubPages();
            }
            return this.subPages;
        }
        return [];
    }

    static styles = [
        accessibility,
        formStyles,
        css`
            allin-image-uploader img[src='']{
                display:none;
            }
            .header, .message:not(:empty){
                position:relative;
                top:-2rem;
                left:-2rem;
                width:calc(100% + 4rem);
                background-color:var(--color-bg,#F7F7F7);
                padding-bottom:2rem;
            }
            
           
        `
    ]
}


